/**
 * Sorting utility functions for alphabetical name ordering.
 *
 * Provides locale-aware, case-insensitive sorting with special character
 * handling: names starting with non-letter characters sort after A-Z entries.
 *
 * @packageDocumentation
 */

/** Matches a string whose first character is a Unicode letter. */
const startsWithLetter = /^\p{L}/u;

/**
 * Compare two items by their `name` property.
 *
 * - Case-insensitive (locale-aware via `String.prototype.localeCompare`).
 * - Names starting with non-letter characters (e.g. `_Mark`, `[Owner] Amy`)
 *   are sorted **after** names starting with A-Z / accented letters.
 * - Numeric sub-strings are compared naturally (`user2` before `user10`).
 *
 * @param a - First item with a `name` property
 * @param b - Second item with a `name` property
 * @returns Negative if `a` should come first, positive if `b` should come first, 0 if equal
 */
export function compareByName(
  a: { name: string },
  b: { name: string },
): number {
  const aIsLetter = startsWithLetter.test(a.name);
  const bIsLetter = startsWithLetter.test(b.name);

  // Letters-first: push non-letter-starting names to the end
  if (aIsLetter && !bIsLetter) return -1;
  if (!aIsLetter && bIsLetter) return 1;

  return a.name.localeCompare(b.name, undefined, {
    sensitivity: 'base',
    numeric: true,
  });
}

/**
 * Return a new array of items sorted alphabetically by `name`.
 *
 * Does **not** mutate the input array.
 *
 * @param items - Array of objects with a `name` property
 * @returns A new sorted array
 */
export function sortByName<T extends { name: string }>(items: T[]): T[] {
  return [...items].sort(compareByName);
}

/**
 * Compare two items by their `label` property.
 *
 * Delegates to {@link compareByName} so all sorting rules (case-insensitive,
 * letters-first, natural numeric) apply identically.
 *
 * @param a - First item with a `label` property
 * @param b - Second item with a `label` property
 * @returns Negative if `a` should come first, positive if `b` should come first, 0 if equal
 */
export function compareByLabel(
  a: { label: string },
  b: { label: string },
): number {
  return compareByName({ name: a.label }, { name: b.label });
}

/**
 * Return a new array of items sorted alphabetically by `label`.
 *
 * Does **not** mutate the input array.
 *
 * @param items - Array of objects with a `label` property
 * @returns A new sorted array
 */
export function sortByLabel<T extends { label: string }>(items: T[]): T[] {
  return [...items].sort(compareByLabel);
}
